import React from 'react';
import { IntercomAPI } from 'react-intercom';
import _isNumber from 'lodash/isNumber';
import _assign from 'lodash/assign';
import _isEmpty from 'lodash/isEmpty';
import _intersection from 'lodash/intersection';
import _forEach from 'lodash/forEach';
import _keys from 'lodash/keys';
import _get from 'lodash/get';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import dayjs from 'dayjs';
import dayjsDuration from 'dayjs/plugin/duration';
import pluralize from 'pluralize';

import { solutionStatus } from '../constants/SolutionStatus';
import { problemTypes } from '../constants/ProblemTypes';
import {
  MIME_TYPE_WEBP,
  MIME_TYPE_JPEG,
  PROCTOR_INITIATED_IMAGE_UPLOAD_LOCALSTORAGE_KEY,
  PROCTOR_TEST_START_TIME_LOCALSTORAGE_KEY,
} from '../constants/Proctor';
import {
  PREDICTION_FILE,
  SOURCE_FILE,
} from '../components/ProblemTypes/FileBasedEvaluation/constants';
import UploadErrorIcon from '../assets/img/error_outline_24px.svg';

import {
  TEST_END_TIME_LOCALSTORAGE_KEY,
  UPLOAD_FAILED_QUESTIONS,
  SECURE_BROWSER_CONST,
} from '../constants/Constants';
import {
  WEBSOCKET_EVENT_SEQUENCE_SESSIONSTORAGE_KEY,
  FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY,
  FAILED_SOCKET_EVENTS_SESSIONSTORAGE_DEFAULT_VALUE,
} from '../constants/Socket';
import { incrementProctorCacheBasedOnKey } from './helperFunction';
import { errorLogger, proctoringUBATracker } from './trackingUtil';
import { EAudioRecordingStatus } from '../components/ProblemTypes/Audio/constants';

// import { intercomAppId } from '../constants/AppConstants';

let offsetTime = 0;
const day = ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'];
const month = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
export function setOffsetTime(offset) {
  offsetTime = _isNumber(offset) ? offset : 0;
}

export function getServerTime() {
  const newDate = new Date();
  if (offsetTime && typeof offsetTime === 'number')
    newDate.setTime(newDate.getTime() + offsetTime);
  return newDate;
}

export const getImageDetailsFromDataURL = dataURL => {
  const [meta, base64] = dataURL.split(',');
  const mime = meta.match(/data:(image\/[^;]+);base64/)[1];

  const padded = base64.length % 4;
  const sizeInBytes = (base64.length * 3) / 4 - (padded ? 4 - padded : 0);

  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      resolve({
        mimeType: mime,
        sizeInBytes: Math.round(sizeInBytes),
        sizeInKB: Number((sizeInBytes / 1024).toFixed(2)),
        width,
        height,
        aspectRatio: Number((width / height).toFixed(3)),
      });
    };
    img.src = dataURL;
  });
};

// We need for oncall debug in production
window.getServerTime = getServerTime;

// import Bowser from 'bowser';
// import moment from 'moment-timezone';
// import html2canvas from 'html2canvas';

/**
 * Function is used to act as a layer on top of React.lazy(), It will
 * try to download a chunk that failed to load initially
 * @param {function} fn function that loads a chunk and returns a promise
 * @param {Number} retriesLeft no of times it will try to reload the chunk
 * @param {Number} interval waiting time until it will retry again
 */
export function retry(fn, retriesLeft = 4, interval = 1500) {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch(error => {
        setTimeout(() => {
          if (retriesLeft === 0) {
            reject(error);
            return;
          }
          // Passing on "reject" is the important part
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}

let Bowser;
let moment;
let html2canvas; // eslint-disable-line
/**
 * Loading these heavy plugins dynamically to reduce bundle size
 */
retry(() => import('moment-timezone')).then(resp => {
  moment = resp;
});

retry(() => import('bowser')).then(bowserResp => {
  Bowser = bowserResp;
});

// retry(() => import('html2canvas')).then(html2canvasResp => {
//   html2canvas = html2canvasResp && html2canvasResp.default;
// });

export function trackEvent(event, metaData) {
  // if (intercomAppId) {
  IntercomAPI('trackEvent', event, metaData || undefined);
  // }
}

export function detectBrowser() {
  if (!Bowser) {
    return '';
  }
  if (window.electron) {
    return `${SECURE_BROWSER_CONST.BROWSER_NAME} ${SECURE_BROWSER_CONST.BROWSER_VERSION}`;
  }
  const { getParser } = Bowser;
  const browser = getParser(window.navigator.userAgent);
  const browserData = browser?.getBrowser();
  const versions = browserData?.version?.split('.');
  const version = versions?.length && versions[0];
  return `${browserData.name} ${version}`;
}

export function getSystemDetails() {
  if (!Bowser) {
    return {};
  }
  const { getParser } = Bowser;
  const { parsedResult } = getParser(window.navigator.userAgent);
  return {
    browser: window.electron
      ? SECURE_BROWSER_CONST.BROWSER_NAME
      : parsedResult.browser.name,
    os: parsedResult.os.name,
    platform: parsedResult.platform.type,
    osVersionName: parsedResult.os.versionName,
  };
}

export function isFirefoxBrowser() {
  const systemDetails = getSystemDetails();
  const firefoxBrowserAlias = ['Firefox', 'Mozilla Firefox'];
  return firefoxBrowserAlias.includes(systemDetails?.browser);
}

export function isSafariBrowser(agent) {
  return /^((?!chrome|android).)*safari/i.test(agent || navigator.userAgent);
}

/**
 * Method used to get UTC time
 */
export function getTime(date, userTimeZone) {
  if (!moment) {
    return new Date();
  }
  if (!date || !userTimeZone) return moment.tz(new Date(), 'UTC');
  return moment.tz(date, 'UTC').tz(userTimeZone);
}

/**
 * If the user's system time is wrong we add the time offset that we
 * calculated from the server's time. If we don't do this the timer
 * for the test will be incorrect
 */
export function getCurrentTime() {
  return new Date(offsetTime + Date.now());
}

export function getTimeString() {
  const date = getCurrentTime();
  return `${day[date.getDay()]} ${date.getDate()} ${
    month[date.getMonth()]
  },${date.getHours()}:${
    date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()
  }`;
}
/**
 * Function used to take a snapshot of the browserwindow
 */
export async function getBrowserScreenShot() {
  if (!html2canvas) {
    return null;
  }
  return html2canvas(document.body);
}

/**
 * getCurrentTimedSection
 * @param {Array} sections testSolutionSet.sections
 * @returns {Object} currentTimedSection
 */
export function getCurrentTimedSection(sections) {
  const currentTimedSection = _find(sections, ['status', 'CTK']);
  return currentTimedSection;
}

/**
 * getPendingTimedSections
 * @param {Array} sections testSolutionSet.sections
 * @returns {Array} Array of pending timed sections
 */
export function getPendingTimedSections(sections) {
  const ctkSectionIndex = sections.findIndex(
    section => section.status === 'CTK'
  );
  let pendingTimedSections;
  if (ctkSectionIndex >= 0)
    pendingTimedSections = sections
      .slice(ctkSectionIndex)
      .filter(section => section.status === 'PND');
  else
    pendingTimedSections = sections.filter(section => section.status === 'PND');
  return pendingTimedSections;
}

/**
 * getSubmittedSections
 * @param {Array} sections testSolutionSet.sections
 * @returns {Array} Array of submitted timed sections
 */
export function getSubmittedSections(sections) {
  const submittedTimedSections = _filter(sections, ['status', 'SUB']);
  return submittedTimedSections;
}

/**
 * isLastTimedSection
 * @param {Array} sections testSolutionSet.sections
 * @returns {Boolean}
 */
export function isLastTimedSection(sections) {
  const totalSections = sections.length;
  const submittedTimedSections = getSubmittedSections(sections).length;
  if (totalSections - submittedTimedSections === 1) {
    return true;
  }

  return false;
}

export const getSectionTotalProblems = section => {
  const raw = section?.problem_count ?? 0;
  if (typeof raw === 'string') {
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  }
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
};

/**
 * Get test total and attempted problem count
 * @param {Array} sections testSolutionSet.sections
 * @param {Object} attemptedProblems state.appStatus.attemptedProblems
 * @returns {Object} Test total problem count and test attempted problem count
 */
export function getTestProblemCount(
  test,
  attemptedProblems,
  solutions,
  testSolutionSet
) {
  let testTotalProblems = _get(test, 'problems_total');
  if (test?.sub_test_type === 'ADP') {
    const secs = Array.isArray(testSolutionSet?.sections)
      ? testSolutionSet.sections
      : [];
    const summed = secs.reduce(
      (acc, sec) => acc + getSectionTotalProblems(sec),
      0
    );
    if (summed > 0) testTotalProblems = summed;
  }
  const testAttemptedProblems =
    test?.sub_test_type === 'ADP'
      ? _keys(solutions || {}).length
      : _keys(attemptedProblems || {}).length;
  return { testTotalProblems, testAttemptedProblems };
}

/**
 * Get test current sections total and attempted problem count
 * @param {Array} sections testSolutionSet.sections
 * @returns {Object} Section total problem count and section attempted problem count
 */
export function getSectionProblemCount(
  test,
  sections,
  attemptedProblems,
  solutions
) {
  const currentTimedSection = getCurrentTimedSection(sections) || {};

  // ADP: attempted should mean "has a saved solution", not merely "visited/shown".
  // `attemptedProblems` is populated on SAVE_SOLUTION success and seeded from
  // section problems that already have `solution` on load.
  if (test?.sub_test_type === 'ADP') {
    const sectionSlug =
      currentTimedSection?.slug ||
      currentTimedSection?.section_slug ||
      currentTimedSection?.sectionSlug ||
      null;
    const sectionMap =
      test?.problem_section_map && typeof test.problem_section_map === 'object'
        ? test.problem_section_map
        : null;
    // As requested: in adaptive, treat `solutions` object keys length as attempted count.
    // We still scope to the current section (when possible) using `problem_section_map`.
    const attemptedKeys = _keys(solutions || {});
    const sectionTotalProblems = getSectionTotalProblems(currentTimedSection);

    // Best case: map attempted slugs -> this section via backend-provided map.
    if (sectionSlug && sectionMap) {
      const sectionAttemptedProblems = attemptedKeys.filter(
        slug => sectionMap?.[slug] === sectionSlug
      ).length;
      return { sectionTotalProblems, sectionAttemptedProblems };
    }

    // Fallback: if there's only one section, treat all attempted as belonging to it.
    if (Array.isArray(sections) && sections.length === 1) {
      return {
        sectionTotalProblems,
        sectionAttemptedProblems: attemptedKeys.length,
      };
    }

    // Otherwise, we can't reliably scope attempted-by-solution to the current section.
    // Start at 0 (matches requirement) until we have mapping data.
    return { sectionTotalProblems, sectionAttemptedProblems: 0 };
  }

  const rawProblems =
    currentTimedSection?.problems ??
    currentTimedSection?.problem_slugs ??
    currentTimedSection?.problemSlugs ??
    [];

  // Non-adaptive timed sections: `problems` is expected to be an array of slugs,
  // but a few APIs send full objects; normalize to slugs.
  const slugs = (Array.isArray(rawProblems) ? rawProblems : [])
    .map(p => {
      if (!p) return null;
      if (typeof p === 'string') return p;
      return p?.slug || p?.problem_slug || p?.problemSlug || null;
    })
    .filter(Boolean);

  const sectionTotalProblems = slugs.length;
  const sectionAttemptedProblems = _intersection(
    _keys(attemptedProblems || {}),
    slugs
  ).length;
  return { sectionTotalProblems, sectionAttemptedProblems };
}

/**
 * Get test or test solution set expiry in UTC
 * @param {Object} test Test
 * @param {Object} testSolutionSet Test solution set
 * @returns {Date} Date in UTC
 */
export function getExpiryTime(test, testSolutionSet) {
  let expiryTime;
  if (!test.is_timed_section) {
    expiryTime = testSolutionSet.expiry;
  } else if (test.is_timed_section) {
    const currentSection = getCurrentTimedSection(testSolutionSet.sections);
    if (!_isEmpty(currentSection)) expiryTime = currentSection.expiry;
  }
  return expiryTime;
}

/**
 * Returns test duration
 * @param {Object} testSolutionSet Test solution set
 * @returns {String} Duration in milliseconds
 */
export function getTestDuration(testSolutionSet) {
  const { expiry } = testSolutionSet;
  const currentTime = getCurrentTime();
  const duration = dayjs(expiry).diff(currentTime);
  return duration;
}

/**
 * Returns test or timed section duration in millisecond
 * @param {Object} test Test
 * @param {Object} testSolutionSet Test solution set
 * @returns {Number} Duration in milliseconds
 */
export function getDuration(test, testSolutionSet) {
  const expiryTime = getExpiryTime(test, testSolutionSet);
  const currentTime = getCurrentTime();
  const duration = dayjs(expiryTime).diff(currentTime);
  return duration;
}

/**
 * Converts duration which is in millisecond to human readble format
 * @param {Number} milliseconds Time in millisecond
 * @returns {String} Duration in human redable format
 */
export function getTimeInHumanFormat(milliseconds) {
  let timeInHumanFormat = '';
  dayjs.extend(dayjsDuration);
  const tempDuration = dayjs.duration(milliseconds);
  const days = Math.floor(tempDuration.asDays());
  const hours = Math.floor(tempDuration.asHours() % 24);
  const minutes = Math.floor(tempDuration.asMinutes() % 60);
  const seconds = Math.floor(tempDuration.asSeconds() % 60);

  if (days) timeInHumanFormat += `${pluralize('day', days, true)} `;
  if (hours) timeInHumanFormat += `${pluralize('hour', hours, true)} `;
  if (minutes) timeInHumanFormat += `${pluralize('minute', minutes, true)} `;
  if (seconds) timeInHumanFormat += `${pluralize('second', seconds, true)}`;
  return timeInHumanFormat;
}

/**
 * Get test or section time taken in human format
 * @param {Object} test Test
 * @param {Object} testSolutionSet Test solution set
 * @returns {String} Time taken in human format
 */
export function getTimeTakenInHumanFormat(test, testSolutionSet) {
  let timeTaken;
  dayjs.extend(dayjsDuration);
  const isLastSection = isLastTimedSection(testSolutionSet.sections);
  if (testSolutionSet.has_resumed_test) {
    // If admin adds extra time for the candidate
    if (!test.is_timed_section) {
      timeTaken = testSolutionSet.add_test_duration;
    } else if (test.is_timed_section && !isLastSection) {
      const { extend_duration } = getCurrentTimedSection(
        testSolutionSet.sections
      );
      timeTaken = extend_duration;
    } else if (test.is_timed_section && isLastSection) {
      timeTaken = testSolutionSet.add_test_duration;
    }
  } else if (!testSolutionSet.has_resumed_test) {
    // Normal test with
    if (!test.is_timed_section) {
      timeTaken = test.duration;
    } else if (test.is_timed_section && !isLastSection) {
      const { duration } = getCurrentTimedSection(testSolutionSet.sections);
      timeTaken = duration;
    } else if (test.is_timed_section && isLastSection) {
      timeTaken = test.duration;
    }
  }

  const timeTakenInHumanFormat = getTimeInHumanFormat(
    dayjs.duration(timeTaken, 'minutes').asMilliseconds()
  );
  return timeTakenInHumanFormat;
}

/**
 * Get time spent in test (timeunit: Milliseconds)
 * @param {Object} testSolutionSet
 * @returns {Number} Milliseconds
 */
export function getTimeSpentInTest(startTime) {
  const currentTime = getCurrentTime();
  const timeSpentInTest = dayjs(currentTime).diff(startTime);
  return timeSpentInTest;
}

// Todo: Break this function logic in to small chunks
export function prepareSubmitMessage(
  test,
  testSolutionSet,
  attemptedProblems,
  solutions,
  testTypeVerbose,
  isTimeout,
  uploadFailedQuestions
) {
  let bodyText;
  let titleText;
  let positiveBtnText;

  const { testTotalProblems, testAttemptedProblems } = getTestProblemCount(
    test,
    attemptedProblems,
    solutions,
    testSolutionSet
  );
  const enableFeedbackPage = _get(test, 'enable_feedback_page', true);

  // for any auto test submit
  if (isTimeout) {
    bodyText = (
      <div>
        This {testTypeVerbose} has been auto-submitted as the allotted time is
        up.
        <br /> <br />
        {!test.isAiInterview && (
          <>
            You attempted <strong>{testAttemptedProblems}</strong> out of{' '}
            <strong>{testTotalProblems}</strong>{' '}
            {pluralize('question', testTotalProblems)} in{' '}
            <strong>{getTimeTakenInHumanFormat(test, testSolutionSet)}</strong>.
          </>
        )}
      </div>
    );
    titleText = 'Thank you!';
    positiveBtnText = `Proceed to ${
      enableFeedbackPage ? 'feedback' : 'next'
    } page`;
  }

  if (!isTimeout && !test.is_timed_section) {
    bodyText = (
      <div className="modalBodyText">
        <div className="bodySubmitInfo">
          You still have{' '}
          <strong>
            {getTimeInHumanFormat(getTestDuration(testSolutionSet))}
          </strong>{' '}
          left to submit this {testTypeVerbose}. <br /> <br /> You have
          attempted <strong>{testAttemptedProblems}</strong> out of{' '}
          <strong>{testTotalProblems}</strong>{' '}
          {pluralize('question', testTotalProblems)}. <br /> <br /> You will not
          be able to attempt any question or make changes to your solutions once
          you submit.
        </div>
        {uploadFailedQuestions?.length && uploadFailedQuestions.length > 0 ? (
          <div className="modalFailedSection">
            <div className="uploadFailedText">
              <img src={UploadErrorIcon} alt="Upload-Error-Icon" />
              {UPLOAD_FAILED_QUESTIONS}
            </div>
            <div className="questionBadges">
              {uploadFailedQuestions.map(questionLabel => (
                <div className="questionLabelBadge">{questionLabel}</div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
    titleText = 'Are you sure?';
    positiveBtnText = `Submit ${testTypeVerbose}`;
  }

  // If test has timed section and user manually submit the test
  if (!isTimeout && test.is_timed_section) {
    dayjs.extend(dayjsDuration);
    // Get current section expiry
    const currentSectionDuration = getDuration(test, testSolutionSet);
    // Holds summation of pending section duration
    let duration = 0;
    // Get all pending timed sections
    const pendingTimedSections = getPendingTimedSections(
      testSolutionSet.sections
    );
    if (
      (testSolutionSet.has_resumed_test === false &&
        !testSolutionSet.resumed_at) ||
      (testSolutionSet.has_resumed_test === true && testSolutionSet.resumed_at)
    ) {
      // If recruiter has increased the test duration for candidate after first attempt
      // then candidate will get only extended time duration instead of duration + extended duration
      _forEach(pendingTimedSections, pendingTimedSection => {
        duration += pendingTimedSection.extend_duration || 0;
      });
    } else {
      // If candidate is attempting the section and currently taking the section then that section
      // plus subsequent section time will become duration+extend_duration
      _forEach(pendingTimedSections, pendingTimedSection => {
        duration +=
          pendingTimedSection.duration +
          (pendingTimedSection.extend_duration || 0);
      });
    }
    // Holds addition of summation of pending section and currentSectionDuration
    const durationInMillisecond =
      dayjs.duration(duration, 'minutes').asMilliseconds() +
      currentSectionDuration;
    bodyText = (
      <div className="modalBodyText">
        <div className="bodySubmitInfo">
          You still have{' '}
          <strong>{getTimeInHumanFormat(durationInMillisecond)}</strong> left to
          submit this {testTypeVerbose}. <br /> <br /> You have attempted{' '}
          <strong>{testAttemptedProblems}</strong> out of{' '}
          <strong>{testTotalProblems}</strong>{' '}
          {pluralize('question', testTotalProblems)}. <br /> <br /> You will not
          be able to attempt any question or make changes to your solutions once
          you submit.
        </div>
        {uploadFailedQuestions?.length && uploadFailedQuestions.length > 0 ? (
          <div className="modalFailedSection">
            <div className="uploadFailedText">
              <img src={UploadErrorIcon} alt="Upload-Error-Icon" />
              {UPLOAD_FAILED_QUESTIONS}
            </div>
            <div className="questionBadges">
              {uploadFailedQuestions.map(questionLabel => (
                <div className="questionLabelBadge">{questionLabel}</div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
    titleText = 'Are you sure?';
    positiveBtnText = `Submit ${testTypeVerbose}`;
  }

  return {
    titleText,
    bodyText,
    positiveBtnText,
  };
}

// Todo: Break this function logic in to small chunks
/**
 * Method used to check if extra time is added to test that has time based sections.
 * Based on the status of the sections need to show the currect message in the modal
 * to the user
 * @param {Array} sections
 * @param {Number} addTestDuration
 */
export function prepareSubmitSectionMessage(
  test,
  testSolutionSet,
  attemptedProblems,
  solutions,
  testTypeVerbose,
  isTimeout,
  uploadFailedQuestions
) {
  const {
    sectionAttemptedProblems,
    sectionTotalProblems,
  } = getSectionProblemCount(
    test,
    testSolutionSet.sections,
    attemptedProblems,
    solutions
  );
  let bodyText;
  let titleText;
  let positiveBtnText;
  const isLastSection = isLastTimedSection(testSolutionSet.sections);

  if (isTimeout && !isLastSection) {
    bodyText = (
      <div className="modalBodyText">
        <div className="bodySubmitInfo">
          This section has been auto-submitted as the allotted time is up.
          <br /> <br />
          {!test.isAiInterview && (
            <>
              You attempted <strong>{sectionAttemptedProblems}</strong> out of{' '}
              <strong>{sectionTotalProblems}</strong>{' '}
              {pluralize('question', sectionTotalProblems)} in{' '}
              <strong>
                {getTimeTakenInHumanFormat(test, testSolutionSet)}
              </strong>
            </>
          )}
        </div>
        {uploadFailedQuestions?.length && uploadFailedQuestions.length > 0 ? (
          <div className="modalFailedSection">
            <div className="uploadFailedText">
              <img src={UploadErrorIcon} alt="Upload-Error-Icon" />
              {UPLOAD_FAILED_QUESTIONS}
            </div>
            <div className="questionBadges">
              {uploadFailedQuestions.map(questionLabel => (
                <div className="questionLabelBadge">{questionLabel}</div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );

    titleText = 'Going great!';

    positiveBtnText = 'proceed to next section';
  }

  if (!isTimeout) {
    bodyText = (
      <div className="modalBodyText">
        <div className="bodySubmitInfo">
          You still have{' '}
          <strong>
            {getTimeInHumanFormat(getDuration(test, testSolutionSet))}
          </strong>{' '}
          left to submit this section.
          <br /> <br />
          You have attempted <strong>{sectionAttemptedProblems}</strong> out of{' '}
          <strong>{sectionTotalProblems}</strong>{' '}
          {pluralize('question', sectionTotalProblems)}.
          <br /> <br />
          You will not be able to revisit this section once you submit and the
          remaining time will not get carried over to the next section.
        </div>
        {uploadFailedQuestions?.length && uploadFailedQuestions.length > 0 ? (
          <div className="modalFailedSection">
            <div className="uploadFailedText">
              <img src={UploadErrorIcon} alt="Upload-Error-Icon" />
              {UPLOAD_FAILED_QUESTIONS}
            </div>
            <div className="questionBadges">
              {uploadFailedQuestions.map(questionLabel => (
                <div className="questionLabelBadge">{questionLabel}</div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );

    titleText = 'Are you sure?';

    positiveBtnText = 'proceed to next section';
  }

  // If timed section auto submits in the last section show test submit message
  if (isTimeout && isLastSection) {
    const testSubmitMessage = prepareSubmitMessage(
      test,
      testSolutionSet,
      attemptedProblems,
      testTypeVerbose,
      true,
      uploadFailedQuestions
    );
    bodyText = testSubmitMessage.bodyText;
    titleText = testSubmitMessage.titleText;
    positiveBtnText = testSubmitMessage.positiveBtnText;
  }

  return {
    bodyText,
    titleText,
    positiveBtnText,
  };
}

/**
 * We only show the true status of a solution in the report page.
 *
 * When the candidate is continuing a test with extra time the data
 * from the backend will have the correct status (meaning if the candidate had
 * choosen the incorrect answer for a MCQ the original status will be Rejected),
 * but we do not show it to the candidate (except in report page).
 * The function gets the following values and returns the status that
 * needs to be displayed
 * @param {String} problemType
 * @param {String} solution any value that tells whether the solution is present or not
 * @param {String} problemStatus
 */
export function prepareProblemStatus(problemType, solution, problemStatus) {
  let status = problemStatus;
  const isSolutionPresent = !!solution;
  switch (problemType) {
    case problemTypes.MCQ:
    case problemTypes.FIB:
      if (isSolutionPresent) status = solutionStatus.UNE;
      else status = undefined;
      break;
    case problemTypes.SUB:
      if (!isSolutionPresent) status = undefined;
      else {
        switch (problemStatus) {
          case solutionStatus.NRE:
            status = solutionStatus.SUBMITTED;
            break;
          default:
            break;
        }
      }
      break;
    case problemTypes.SCR:
    case problemTypes.DSC:
    case problemTypes.MLI:
    case problemTypes.SEL:
    case problemTypes.DBA:
    case problemTypes.FBE:
      if (isSolutionPresent) {
        status = ['REJ', 'ACC', 'NRE', 'UNE', 'PAC'].includes(problemStatus)
          ? status
          : solutionStatus.UNE;
        if (status === 'NRE') status = solutionStatus.SUBMITTED;
      } else status = undefined;
      break;
    case problemTypes.UIX:
    case problemTypes.PRJ:
      if (isSolutionPresent) {
        status = ['REJ', 'ACC', 'NRE', 'UNE', 'PAC'].includes(problemStatus)
          ? status
          : solutionStatus.UNE;
        if (status === 'NRE') status = solutionStatus.SUBMITTED;
      } else status = undefined;
      break;
    case problemTypes.VID:
    case problemTypes.ADO:
      if (isSolutionPresent) status = solutionStatus.UNE;
      else status = undefined;
      break;
    default:
      break;
  }
  return status;
}

const prepareUrl = (testSlug, problemSlug, userId) =>
  `DoSelect.${window.location.origin}/test/${testSlug}/problem/${problemSlug}-${userId}`;

export function getCodeFromStorage(testSlug, problemSlug, userId) {
  const key = prepareUrl(testSlug, problemSlug, userId);
  let data = localStorage.getItem(key);
  try {
    data = data
      ? JSON.parse(data)
      : {
          language: '',
          stub: '',
          stdout: '',
          stdin: '',
          code: '',
          saveTime: '',
          log: '',
        };
  } catch (e) {
    data = {
      language: '',
      stub: '',
      stdout: '',
      stdin: '',
      code: '',
      saveTime: '',
      log: '',
    };
  }
  return data;
}

export function setCodeToStorage(testSlug, problemSlug, userId, data) {
  const key = prepareUrl(testSlug, problemSlug, userId);
  let existing = localStorage.getItem(key);
  let newValue = {};
  if (existing) {
    try {
      existing = JSON.parse(existing);
      newValue = _assign({}, existing, data);
    } catch (e) {
      newValue = {};
    } finally {
      newValue = JSON.stringify(newValue);
    }
  } else {
    newValue = _assign(
      {},
      {
        language: '',
        stub: '',
        stdout: '',
        stdin: '',
        code: '',
        saveTime: '',
        log: '',
      },
      data
    );
    newValue = JSON.stringify(newValue);
  }
  localStorage.setItem(key, newValue);
}

export const emptyClipboard = inputElement => {
  if (inputElement) {
    if (_get(navigator, 'clipboard.writeText')) {
      navigator.clipboard.writeText('').then(
        () => {},
        () => {}
      );
    }
    /**
     * Fallback in case the above is not supported
     */
    inputElement.style.display = 'block'; // eslint-disable-line no-param-reassign
    if (inputElement.select) inputElement.select();
    else if (inputElement.setSelectionRange)
      inputElement.setSelectionRange(0, 9);
    document.execCommand('copy');
    inputElement.style.display = 'none'; // eslint-disable-line no-param-reassign
  }
};

export const dataURItoBlob = dataURI => {
  if (!dataURI) return null;

  // Split metadata and base64 data
  const parts = dataURI.split(',');
  const meta = parts[0] || '';
  const base64 = parts[1] || '';

  const mimeMatch = meta.match(/data:([^;]+);base64/);
  const mimeType = mimeMatch ? mimeMatch[1] : null;

  const blobType =
    mimeType === MIME_TYPE_WEBP ? MIME_TYPE_WEBP : MIME_TYPE_JPEG;

  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: blobType });
};

export const blankToDash = description => {
  const reg = /{BLANK}/g;
  const blank = '__________'; // 10 underscores;

  return description.replace(reg, blank);
};

export const getFileBasedPayloadSchema = ({
  fieldName,
  fileBasedData,
  url,
}) => {
  let fileUri = {};
  if (PREDICTION_FILE === fieldName) fileUri = { main_url: url };
  else if (SOURCE_FILE === fieldName) fileUri = { supplementary_url: url };

  const problemSchema = {
    main_url: '',
    revisions: [],
    supplementary_url: '',
    uploaded_at: new Date().toISOString(),
    ...fileBasedData,
    ...fileUri,
  };

  return problemSchema;
};

export const getTimeDiffBwTimezone = time => {
  if (moment) {
    const curr = moment.utc(new Date());

    const next = moment.utc(time);

    const duration = moment.duration(curr.diff(next));

    return {
      secs: duration.seconds(),
      mins: duration.minutes(),
      hrs: duration.hours(),
    };
  }
  return {
    secs: null,
    mins: null,
    hrs: null,
  };
};

export const getEndTimeLocalStorageKey = assessmentId => {
  return `${assessmentId}${TEST_END_TIME_LOCALSTORAGE_KEY}`;
};

export const customSortByname = (first, second) => {
  if (first.name < second.name) return -1;
  if (first.name > second.name) return 1;
  return 0;
};

export const getWebsocketSequenceStorageKey = assessmentId => {
  return `${WEBSOCKET_EVENT_SEQUENCE_SESSIONSTORAGE_KEY}${assessmentId}`;
};

export const getAndUpdateWebsocketSequence = assessmentId => {
  const sequenceKey = getWebsocketSequenceStorageKey(assessmentId);
  const eventSequence = sessionStorage.getItem(sequenceKey);
  incrementProctorCacheBasedOnKey(sequenceKey, sessionStorage);
  return eventSequence;
};

/**
 * Returns failed socket events filtered by the provided event names
 * @param {Array<string>} eventNames
 * @returns {Array<object>} Array of failed socket event items
 */
export const getFailedSocketEventsByNames = (eventNames = []) => {
  try {
    const failedSocketEventsData = sessionStorage.getItem(
      FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY
    );
    const failedEventsValue = failedSocketEventsData
      ? JSON.parse(failedSocketEventsData)
      : FAILED_SOCKET_EVENTS_SESSIONSTORAGE_DEFAULT_VALUE;

    if (failedEventsValue && typeof failedEventsValue === 'object') {
      return Object.values(failedEventsValue).filter(eventItem => {
        const eventName = eventItem?.socketEventName;
        return eventNames.includes(eventName);
      });
    }
    return [];
  } catch (failedSocketEventsReadError) {
    errorLogger(failedSocketEventsReadError);
    return [];
  }
};

export const initiateSignedUrlLogging = source => {
  try {
    let proctorStartTime = localStorage.getItem(
      PROCTOR_TEST_START_TIME_LOCALSTORAGE_KEY
    );
    const currentTime = getCurrentTime();
    proctorStartTime = new Date(proctorStartTime);
    let expectedImagesInitiated = (currentTime - proctorStartTime) / 1000;
    expectedImagesInitiated = Number(expectedImagesInitiated);
    let imagesInitiated = localStorage.getItem(
      PROCTOR_INITIATED_IMAGE_UPLOAD_LOCALSTORAGE_KEY
    );
    imagesInitiated = Number(imagesInitiated);
    const percentageMiss =
      ((expectedImagesInitiated - imagesInitiated) / expectedImagesInitiated) *
      100;

    if (percentageMiss >= 80 && percentageMiss <= 100) {
      proctoringUBATracker({
        additionalInfo: `failedInitiate-${source}-cadidateCpuCore:${
          navigator?.hardwareConcurrency
        }-${expectedImagesInitiated}-${localStorage.getItem(
          PROCTOR_INITIATED_IMAGE_UPLOAD_LOCALSTORAGE_KEY
        )}`,
      });
    }
  } catch (err) {
    errorLogger(err);
  }
};

export const detectMobile = () => {
  const toMatch = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ];

  return toMatch.some(toMatchItem => navigator.userAgent.match(toMatchItem));
};

export const base64ToBlob = (dataURI, mime) => {
  const base64 = dataURI.split('base64,')[1];
  const byteCharacters = window.atob(base64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mime });
};

export const blobToBase64 = blob => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const getFromLocalStorage = key => {
  return localStorage.getItem(key);
};

export const formatTimeInMinSec = timeInSeconds => {
  const minutes = Math.floor(timeInSeconds / 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.ceil(timeInSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${minutes}:${seconds}`;
};

export const getFailedUploadQuestionNumbers = (
  failedSlugs,
  indexedProblems
) => {
  return failedSlugs.map(slug => `Question ${indexedProblems[slug]}`);
};

export const combineFailedSlugs = (audioUploadSlugs, videoUploadSlugs) => {
  return Object.keys({
    ...audioUploadSlugs,
    ...videoUploadSlugs,
  }).reduce((failedSlugs, key) => {
    if (
      audioUploadSlugs[key] === EAudioRecordingStatus.FAILED ||
      videoUploadSlugs[key]
    ) {
      failedSlugs.push(key);
    }
    return failedSlugs;
  }, []);
};

export const requestMicPermission = async (
  audioTrackConstrains,
  successCb,
  errorCb,
  isMobile = false
) => {
  try {
    const micPromise = navigator.mediaDevices.getUserMedia({
      audio: audioTrackConstrains ?? true,
    });

    const mStream = isMobile
      ? await Promise.race([
          micPromise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('MIC_PERMISSION_TIMEOUT')), 1000)
          ),
        ])
      : await micPromise;
    return successCb(mStream);
  } catch (error) {
    return errorCb(error);
  }
};

export const deleteIndexedDB = async dbName => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(dbName);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const deleteCookie = (cookieKey, domain) => {
  if (domain) {
    document.cookie = `${cookieKey}=; Domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  } else {
    document.cookie = `${cookieKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
};

export const getSubDomain = () => {
  const parts = window.location.hostname.split('.');
  if (parts.length > 2) {
    return `.${parts.slice(1).join('.')}`;
  }
  return `.${window.location.hostname}`;
};
/**
 * Returns the day of the month with an ordinal suffix (e.g., 1st, 2nd, 3rd, 4th...)
 * @param {number} day
 * @returns {string}
 */
export function getDayWithOrdinal(dayName) {
  const ordinals = ['th', 'st', 'nd', 'rd'];
  const v = dayName % 100;
  return dayName + (ordinals[(v - 20) % 10] || ordinals[v] || ordinals[0]);
}

/**
 * Formats a date according to a format string (supports: Do, MMMM, MMM, YYYY, h, mm, A)
 * @param {string} [format='Do MMMM, YYYY'] - Format string (e.g., 'Do MMMM, YYYY')
 * @param {Date} [date=new Date()] - The date to format
 * @returns {string}
 */
export function getFormattedDate(format = 'Do MMMM, YYYY', date = new Date()) {
  // Supported tokens: Do, MMMM, MMM, YYYY
  const dayName = date.getDate();
  const dayWithOrdinal = getDayWithOrdinal(dayName);
  const monthLong = date.toLocaleString('default', { month: 'long' });
  const monthShort = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();
  return format
    .replace(/Do/g, dayWithOrdinal)
    .replace(/MMMM/g, monthLong)
    .replace(/MMM/g, monthShort)
    .replace(/YYYY/g, year);
}

/**
 * Formats a time according to a format string (supports: h, mm, A)
 * @param {string} [format='h:mm A'] - Format string (e.g., 'h:mm A')
 * @param {Date} [date=new Date()] - The date to format
 * @returns {string}
 */
export function getFormattedTime(format = 'h:mm A', date = new Date()) {
  // Supported tokens: h, mm, A
  const hours = date.getHours();
  const minutes = date
    .getMinutes()
    .toString()
    .padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  let hour12 = hours % 12;
  if (hour12 === 0) hour12 = 12;
  return format
    .replace(/h/g, hour12)
    .replace(/mm/g, minutes)
    .replace(/A/g, ampm);
}

export const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

export const isIOSWebView = () => {
  return (
    typeof webkit !== 'undefined' &&
    webkit.messageHandlers?.isWebView?.postMessage
  );
};

export const requestMicPermissionIOSWebview = () => {
  window.webkit.messageHandlers.appSettingPermissionsHandler.postMessage({
    ctaName: 'showEnableMicPermissionAlert',
    keepWebview: true,
    headerMsg: '',
  });
};

export const checkMicPermissionStatusIOSWebview = () => {
  window.webkit.messageHandlers.appSettingPermissionsHandler.postMessage({
    ctaName: 'checkMicPermissionStatus',
  });
};

export const attachOnMicPermissionIOSWebview = ({
  audioConstrains,
  successCb,
  errorCb,
  isMobileDevice,
}) => {
  window.onMicPermissionStatus = async micPermissionStatus => {
    window.onMicPermissionStatus = null;
    if (micPermissionStatus) {
      await requestMicPermission(
        audioConstrains,
        successCb,
        errorCb,
        isMobileDevice
      );
    } else {
      requestMicPermissionIOSWebview();
    }
  };
};

export const iosWebViewRecordHandler = ({
  audioConstrains,
  successCb,
  errorCb,
  isMobileDevice,
}) => {
  attachOnMicPermissionIOSWebview({
    audioConstrains,
    successCb,
    errorCb,
    isMobileDevice,
  });
  checkMicPermissionStatusIOSWebview();
};

export const capitalizeFirstLetter = string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
